import { Alert, AlertTitle, Link as MuiLink, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { stringify } from 'qs';
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { encodeQueryParams, JsonParam, StringParam } from 'use-query-params';

import { compact } from 'es-toolkit/compat';
import { useCurrentLabId } from 'utils/useCurrentLab';

import { getStudyIngestNotice } from './ingestNotice';
import { useStudyIngestStatus } from './useStudyIngestStatus';

const pluralizeSlides = (count: number, singular: string, plural: string) => (count === 1 ? singular : plural);

const getUploadsDashboardUrl = (labId: string, studyId: string) =>
  `/uploads-dashboard?${stringify(
    encodeQueryParams({ filters: JsonParam, labId: StringParam }, { filters: { studyId }, labId })
  )}`;

interface CaseManifestIngestNoticeProps {
  studyId: string;
  /** Whether any ingested slide in this study still needs a case. Omit while that count is unknown (loading or errored). */
  hasUnassignedSlides?: boolean;
}

export const CaseManifestIngestNotice = ({ studyId, hasUnassignedSlides }: CaseManifestIngestNoticeProps) => {
  const { labId } = useCurrentLabId();
  const queryClient = useQueryClient();
  const { uploadList, isLoading } = useStudyIngestStatus(studyId);

  const ingestedCount = uploadList ? getStudyIngestNotice(uploadList).ingested : undefined;
  const observedIngest = useRef<{ scope: string; ingested: number } | null>(null);
  useEffect(() => {
    if (ingestedCount === undefined) return;
    const scope = `${labId}:${studyId}`;
    const previous = observedIngest.current;
    observedIngest.current = { scope, ingested: ingestedCount };
    // Slides that finish ingesting while the chooser is open must also reach the pending-slide counts and tables.
    if (previous?.scope === scope && previous.ingested !== ingestedCount) {
      void queryClient.invalidateQueries({ queryKey: ['pendingSlides'], exact: false });
      void queryClient.invalidateQueries({ queryKey: ['pendingSlidesCount'], exact: false });
    }
  }, [ingestedCount, labId, studyId, queryClient]);

  // Loading and failed-request states are both silent: the notice only adds guidance once the real
  // status is known, and must never show next to already-rendered actions as a transient placeholder.
  if (isLoading || typeof uploadList === 'undefined') return null;

  const reviewUploadsLink = (
    <MuiLink component={Link} to={getUploadsDashboardUrl(labId, studyId)} color="inherit">
      Review uploads
    </MuiLink>
  );

  const { ingesting, queued, failed, rejected, ingested, inProgress, notIngested, hasNoUploads } =
    getStudyIngestNotice(uploadList);

  if (hasNoUploads) {
    return (
      <Alert severity="info" role="status" aria-label="No uploads">
        No slides have been uploaded to this study. Select slides from other studies, or use Add Slides to upload.
      </Alert>
    );
  }

  if (inProgress === 0 && notIngested === 0) {
    if (ingested === 0) return null;
    return (
      <Alert severity="success" role="status" aria-label="All uploads ingested">
        {hasUnassignedSlides === false
          ? 'All uploaded slides are ingested and already assigned to cases.'
          : 'All uploaded slides are ingested and ready to assign.'}
      </Alert>
    );
  }

  const inProgressBreakdown = compact([
    ingesting ? `${ingesting} ${pluralizeSlides(ingesting, 'slide', 'slides')} ingesting` : null,
    queued ? `${queued} ${pluralizeSlides(queued, 'slide', 'slides')} queued` : null,
  ]).join(' · ');
  const notIngestedBreakdown = compact([
    failed ? `${failed} ingest failed` : null,
    rejected ? `${rejected} rejected ${pluralizeSlides(rejected, 'ingest', 'ingests')}` : null,
  ]).join(' · ');

  return (
    <Stack spacing={1}>
      {inProgress > 0 && (
        <Alert severity="info" role="status" aria-label="Ingest in progress">
          <AlertTitle>Ingest in progress</AlertTitle>
          {inProgressBreakdown}. Case assignment opens once ingest finishes.
        </Alert>
      )}
      {notIngested > 0 && (
        <Alert severity="warning" role="status" aria-label="Uploads not ingested">
          <AlertTitle>Uploads not ingested</AlertTitle>
          {notIngested} {pluralizeSlides(notIngested, 'upload has', 'uploads have')} not ingested (
          {notIngestedBreakdown}). {reviewUploadsLink} to retry.
        </Alert>
      )}
    </Stack>
  );
};
